/**
 * Constantes partagées client / serveur / MCP.
 *
 * Règle du projet : TOUTE constante de gameplay vit ici (spec technique §3).
 */

/** Version du protocole applicatif (client ↔ serveur). */
export const PROTOCOL_VERSION = 1;

/** Fréquence de la simulation déterministe, en Hz (spec §3). */
export const TICK_RATE = 60;

/** Durée d'un tick de simulation, en secondes (pas fixe). */
export const TICK_DT = 1 / TICK_RATE;

/** Fréquence d'envoi des snapshots serveur → clients, en Hz (spec §5.2). */
export const SNAPSHOT_RATE = 20;

/** Nombre de ticks de simulation entre deux snapshots (60 / 20 = 3). */
export const SNAPSHOT_INTERVAL_TICKS = TICK_RATE / SNAPSHOT_RATE;

/** Portée du champ magnétique, en tuiles (concept §3.3). */
export const MAGNET_RANGE_TILES = 6;

/** Longueur d'un code de room (spec §5.2). */
export const ROOM_CODE_LENGTH = 5;

/** Alphabet des codes de room : lettres sans ambiguïté visuelle (spec §5.2). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';

/** Durée de vie d'une room restée en lobby, en secondes (spec §5.2). */
export const ROOM_LOBBY_TTL_SECONDS = 15 * 60;

/** Délai de grâce accordé à un joueur déconnecté, en secondes (spec §5.2). */
export const DISCONNECT_GRACE_SECONDS = 30;

/** Port HTTP par défaut du serveur (spec §10.2). */
export const DEFAULT_SERVER_PORT = 3000;

/** Identifiant du seul jeu livré en V1. */
export const MAGNETIC_DUO_GAME_ID = 'magnetic-duo';

// ---------------------------------------------------------------------------
// Réseau (spec §5) — le protocole complet vit dans `schemas/net-messages.ts`.
// ---------------------------------------------------------------------------

/** Chemin du point d'entrée WebSocket servi par Fastify. */
export const WS_PATH = '/ws';

/** Nombre de places dans une room : toujours un duo. */
export const ROOM_CAPACITY = 2;

/** Durée du décompte avant le départ, en secondes (concept §6.2). */
export const COUNTDOWN_SECONDS = 3;

/**
 * Retard d'interpolation du partenaire et des entités, en millisecondes
 * (spec §5.2 : « interpole (100 ms de buffer) »).
 */
export const INTERPOLATION_DELAY_MS = 100;

/** Nombre maximal de snapshots conservés dans le tampon d'interpolation. */
export const INTERPOLATION_BUFFER_SIZE = 32;

/**
 * Nombre maximal d'entrées non confirmées conservées par le client pour la
 * réconciliation (≈ 2 s à 60 Hz). Au-delà, le client est de toute façon
 * désynchronisé et se recale sur le serveur.
 */
export const PREDICTION_MAX_PENDING_INPUTS = 120;

/** Rattrapage maximal de ticks en un seul appel de boucle (client et serveur). */
export const MAX_CATCHUP_TICKS = 8;

/**
 * Période d'émission d'une entrée inchangée, en ticks (10 Hz).
 *
 * Le flux d'entrées est « le dernier reçu gagne » : il suffit d'émettre à
 * CHAQUE changement, plus un rappel périodique qui garantit au serveur une
 * entrée fraîche même si un paquet a été perdu.
 */
export const INPUT_KEEPALIVE_TICKS = 6;

/** Nombre d'essais de tirage d'un code de room avant abandon. */
export const ROOM_CODE_MAX_ATTEMPTS = 64;

/** Clé de stockage local de la session réseau (reprise après rechargement). */
export const NET_SESSION_STORAGE_KEY = 'md:net-session';

// ---------------------------------------------------------------------------
// Contrôles tactiles (concept §5.2).
// ---------------------------------------------------------------------------

/** Largeur de viewport (px) en dessous de laquelle les contrôles tactiles s'activent. */
export const TOUCH_VIEWPORT_MAX_WIDTH_PX = 820;

/** Rayon du joystick virtuel, en pixels CSS. */
export const TOUCH_JOYSTICK_RADIUS_PX = 56;

/**
 * Fraction du rayon en deçà de laquelle le joystick est considéré au repos.
 * Au-delà, la vitesse est remappée progressivement de 0 (bord de la zone
 * morte) à 1 (rayon complet) — jamais de saut de 0 à pleine vitesse.
 */
export const TOUCH_JOYSTICK_DEAD_ZONE = 0.08;

// ---------------------------------------------------------------------------
// Physique — toutes les grandeurs sont en TUILES (et tuiles/seconde), jamais en
// pixels : la conversion en px n'a lieu qu'au rendu (spec §3).
// ---------------------------------------------------------------------------

/** Largeur de la hitbox d'un robot, en tuiles (spec §3). */
export const PLAYER_WIDTH_TILES = 0.8;

/** Hauteur de la hitbox d'un robot, en tuiles (spec §3). */
export const PLAYER_HEIGHT_TILES = 0.9;

/** Gravité appliquée aux corps, en tuiles/s². */
export const GRAVITY_TILES_PER_S2 = 45;

/** Vitesse horizontale de marche, en tuiles/s. */
export const MOVE_SPEED_TILES_PER_S = 6;

/** Vitesse verticale initiale d'un saut, en tuiles/s (vers le haut). */
export const JUMP_SPEED_TILES_PER_S = 15;

/** Vitesse de chute maximale, en tuiles/s (anti-tunneling). */
export const MAX_FALL_SPEED_TILES_PER_S = 24;

/**
 * Hauteur théorique du sommet d'un saut, en tuiles : `v² / (2g)`.
 *
 * L'intégrateur du moteur est trapézoïdal (exact pour une accélération
 * constante) : la valeur est atteinte au tick `TICK_RATE * v / g`.
 */
export const JUMP_APEX_TILES =
  (JUMP_SPEED_TILES_PER_S * JUMP_SPEED_TILES_PER_S) / (2 * GRAVITY_TILES_PER_S2);

/** Délai (en ticks) entre la mort d'un robot et la réinitialisation du niveau. */
export const RESPAWN_DELAY_TICKS = 45;

/** Taille de tuile par défaut au rendu, en pixels. */
export const DEFAULT_TILE_SIZE = 32;

/** Tolérance numérique des tests de collision (unités tuiles). */
export const PHYSICS_EPSILON = 1e-9;

/** Écart maximal (en tuiles) pour considérer un corps posé SUR une entité. */
export const CARRY_CONTACT_EPSILON = 0.02;

// ---------------------------------------------------------------------------
// Magnétisme — jauge d'énergie (concept §3.3).
//
// L'énergie est normalisée dans [0, 1] : 1 = jauge pleine. Elle se vide en
// `1 / MAGNET_DRAIN_PER_SECOND` secondes de champ continu, et se recharge en
// `1 / MAGNET_RECHARGE_PER_SECOND` secondes au repos.
// ---------------------------------------------------------------------------

/** Valeur maximale de la jauge d'énergie (normalisée). */
export const MAGNET_ENERGY_MAX = 1;

/** Vidage de la jauge, par seconde de champ actif → 3 s d'autonomie. */
export const MAGNET_DRAIN_PER_SECOND = 1 / 3;

/** Recharge de la jauge, par seconde au repos → 2 s pour refaire le plein. */
export const MAGNET_RECHARGE_PER_SECOND = 1 / 2;

/**
 * Seuil de ré-armement : après une coupure (jauge vide), le champ ne peut pas
 * être réactivé tant que la jauge n'a pas remonté à cette valeur. Évite le
 * « pompage » d'un joueur qui martèle la touche.
 */
export const MAGNET_ENERGY_REARM = 0.25;

// ---------------------------------------------------------------------------
// Magnétisme — forces entre les deux robots (concept §3.1).
//
// Loi retenue : accélération ∝ 1/d, clampée aux deux bouts (spec §3).
//   a(d) = clamp(MAGNET_FORCE_CONSTANT / max(d, MAGNET_MIN_DISTANCE_TILES),
//                0, MAGNET_MAX_ACCEL_TILES_PER_S2)
// ---------------------------------------------------------------------------

/** Numérateur de la loi de force, en tuiles²/s². */
export const MAGNET_FORCE_CONSTANT = 360;

/** Distance minimale utilisée dans la loi de force (évite la singularité). */
export const MAGNET_MIN_DISTANCE_TILES = 0.9;

/** Accélération magnétique maximale entre deux robots, en tuiles/s². */
export const MAGNET_MAX_ACCEL_TILES_PER_S2 = 220;

/** Vitesse maximale communicable par le magnétisme, en tuiles/s. */
export const MAGNET_MAX_SPEED_TILES_PER_S = 20;

/**
 * Fraction de force HORIZONTALE reçue par un robot posé au sol : l'adhérence
 * absorbe le reste. Un robot accroché à une surface (`clinging`) est, lui,
 * totalement ancré et ne reçoit aucune force.
 */
export const MAGNET_GROUND_GRIP = 0.35;

/** Amortissement de la vitesse relative quand les robots sont au contact. */
export const MAGNET_CONTACT_DAMPING = 0.2;

/** Multiplicateur de la répulsion (pôles identiques) — le saut propulsé. */
export const MAGNET_REPEL_FACTOR = 1.25;

/**
 * Épaisseur maximale de matière solide que le champ traverse, en tuiles
 * (concept §3.1 : « l'attraction fonctionne à travers les murs fins »).
 */
export const MAGNET_WALL_MAX_THICKNESS_TILES = 1.5;

/** Pas d'échantillonnage du segment entre deux corps, en tuiles. */
export const MAGNET_RAY_STEP_TILES = 0.1;

/** Durée d'une inversion de polarité déclenchée par une dalle `!`, en ticks. */
export const POLARITY_INVERT_TICKS = 8 * TICK_RATE;

// ---------------------------------------------------------------------------
// Magnétisme — objets de l'environnement (concept §3.2).
// ---------------------------------------------------------------------------

/** Côté de la hitbox d'un bloc métallique, en tuiles. */
export const BLOCK_SIZE_TILES = 0.9;

/** Numérateur de la loi de force appliquée aux blocs, en tuiles²/s². */
export const BLOCK_FORCE_CONSTANT = 220;

/** Distance minimale utilisée dans la loi de force des blocs. */
export const BLOCK_MIN_DISTANCE_TILES = 1.2;

/** Accélération magnétique maximale d'un bloc, en tuiles/s². */
export const BLOCK_MAX_ACCEL_TILES_PER_S2 = 170;

/** Amortissement de la vitesse d'un bloc sous influence, par tick. */
export const BLOCK_MAGNET_DAMPING = 0.08;

/** Traînée horizontale permanente d'un bloc, par tick (frottement). */
export const BLOCK_DRAG = 0.12;

/** Vitesse maximale d'un bloc, en tuiles/s. */
export const BLOCK_MAX_SPEED_TILES_PER_S = 12;

/** Portée d'un électroaimant fixé au décor, en tuiles. */
export const ELECTROMAGNET_RANGE_TILES = 6;

/** Numérateur de la loi de force d'un électroaimant, en tuiles²/s². */
export const ELECTROMAGNET_FORCE_CONSTANT = 300;

/** Durée par défaut d'une demi-période d'électroaimant cyclique, en secondes. */
export const ELECTROMAGNET_DEFAULT_CYCLE_SECONDS = 3;

/** Distance minimale pour actionner un levier polarisé « à distance ». */
export const LEVER_MIN_DISTANCE_TILES = 1.5;

/** Portée d'action d'un levier polarisé, en tuiles. */
export const LEVER_RANGE_TILES = 6;

// ---------------------------------------------------------------------------
// Entités génériques du moteur.
// ---------------------------------------------------------------------------

/** Vitesse de déplacement d'un ascenseur, en tuiles/s. */
export const LIFT_SPEED_TILES_PER_S = 2.5;

/** Hauteur de la plaque d'un bouton-poussoir, en tuiles. */
export const BUTTON_HEIGHT_TILES = 0.2;

/** Côté de la hitbox d'un collectible (boulon), en tuiles. */
export const COLLECTIBLE_SIZE_TILES = 0.5;

/** Nombre maximal d'étoiles de fin de niveau (concept §4.2). */
export const MAX_STARS = 3;

// ---------------------------------------------------------------------------
// Persistance, API de contenu et serveur MCP (phase 4, spec §6/§7).
// ---------------------------------------------------------------------------

/** Préfixe de l'API REST de lecture du contenu. */
export const API_PREFIX = '/api';

/** Point d'entrée du serveur MCP de contenu (spec §6). */
export const MCP_PATH = '/mcp';

/** Chemin du fichier SQLite par défaut (surchargé par `DB_PATH`, spec §10.2). */
export const DEFAULT_DB_PATH = 'data/magnetic-duo.db';

/** Query string activant la lecture des drafts (Bearer requis, spec §6). */
export const DRAFTS_QUERY_PARAM = 'drafts';

// ---------------------------------------------------------------------------
// OAuth 2.1 du serveur MCP (phase 6).
//
// claude.ai n'offre aucun champ « jeton Bearer » pour un connecteur custom :
// il n'accepte qu'un serveur sans authentification, ou OAuth. Ces chemins sont
// ceux que Claude sonde (RFC 9728 pour la ressource, RFC 8414 pour le serveur
// d'autorisation) ; ils vivent ici pour que les tests de bout en bout les
// nomment sans les recopier.
// ---------------------------------------------------------------------------

/** Métadonnées de la ressource protégée, variante suffixée (RFC 9728). */
export const OAUTH_PROTECTED_RESOURCE_MCP_PATH = '/.well-known/oauth-protected-resource/mcp';

/** Métadonnées de la ressource protégée, variante racine (RFC 9728). */
export const OAUTH_PROTECTED_RESOURCE_PATH = '/.well-known/oauth-protected-resource';

/** Métadonnées du serveur d'autorisation (RFC 8414). */
export const OAUTH_AUTHORIZATION_SERVER_PATH = '/.well-known/oauth-authorization-server';

/** Page de consentement (GET) et soumission du secret (POST). */
export const OAUTH_AUTHORIZE_PATH = '/authorize';

/** Échange du code d'autorisation / rafraîchissement (RFC 6749). */
export const OAUTH_TOKEN_PATH = '/token';

/** Enregistrement dynamique de client, en repli de CIMD (RFC 7591). */
export const OAUTH_REGISTER_PATH = '/register';

/** Portée unique demandée par le connecteur MCP. */
export const OAUTH_SCOPE = 'mcp';

// ---------------------------------------------------------------------------
// Solveur de niveaux (spec §8).
// ---------------------------------------------------------------------------

/** Budget total du solveur pour un niveau, en millisecondes (spec §8). */
export const SOLVER_TIMEOUT_MS = 5000;

/** Garde-fou : nombre maximal d'états joints explorés par l'étage A. */
export const SOLVER_MAX_STATES = 400_000;

/** Nombre maximal de ticks rejoués par l'étage B (60 s de jeu). */
export const SOLVER_REPLAY_MAX_TICKS = 3600;

/** Ticks accordés à UNE transition du plan pendant le rejeu (4 s). */
export const SOLVER_REPLAY_STEP_TICKS = 240;

/** Tolérance d'arrivée sur la tuile visée pendant le rejeu, en tuiles. */
export const SOLVER_ARRIVAL_TOLERANCE_TILES = 0.15;

/**
 * Marge de sécurité appliquée à la portée de saut calculée par l'étage A.
 *
 * L'étage A raisonne sur une trajectoire idéale ; le pilote du rejeu (étage B)
 * ne vise pas au centième de tuile près. Réduire la portée annoncée évite de
 * proposer des sauts « tout juste » que le rejeu échouerait à reproduire.
 */
export const SOLVER_JUMP_MARGIN_TILES = 0.35;

/** Coût d'une arête solo dans la recherche de plan (Dijkstra). */
export const SOLVER_SOLO_EDGE_COST = 1;

/**
 * Coût d'une arête coopérative. Volontairement élevé : le plan retenu privilégie
 * les trajets solo, et le nombre d'arêtes coopératives qu'il contient malgré
 * tout mesure la coopération réellement NÉCESSAIRE (spec §8, métrique 5).
 */
export const SOLVER_COOP_EDGE_COST = 25;

/**
 * Marge retranchée à la portée du champ quand le solveur propose un hissage.
 *
 * À la portée maximale exacte la force est trop faible pour soulever le
 * partenaire de façon fiable ; l'étage A ne propose donc que des ancrages
 * franchement dans la portée.
 */
export const SOLVER_HOIST_RANGE_MARGIN_TILES = 0.5;

/**
 * Marge exigée sur l'accélération verticale d'un hissage.
 *
 * Le partenaire ne soulève réellement le robot tiré que si la composante
 * VERTICALE de la force dépasse la gravité : à angle rasant, l'attraction ne
 * fait que le traîner vers le vide. L'étage A n'accepte donc un ancrage que si
 * `a(d)·dy/d ≥ g × cette marge`.
 */
export const SOLVER_HOIST_LIFT_MARGIN = 1.3;

/**
 * Vitesse verticale retenue par le solveur pour un SAUT PROPULSÉ par répulsion.
 *
 * La répulsion pousse le robot jusqu'à `MAGNET_MAX_SPEED_TILES_PER_S` le long
 * de l'axe des deux robots, et continue de le pousser tant qu'il reste à
 * portée : modéliser le départ comme une impulsion unique de cette vitesse est
 * donc **conservateur** (la poussée réelle porte plus loin).
 */
export const SOLVER_REPEL_LAUNCH_SPEED_TILES_PER_S = MAGNET_MAX_SPEED_TILES_PER_S;

/** Distance maximale entre les deux robots pour un saut propulsé, en tuiles. */
export const SOLVER_REPEL_MAX_RANGE_TILES = 4.5;

/** Dénivelé minimal exigé entre le propulsé et son partenaire, en tuiles. */
export const SOLVER_REPEL_MIN_DROP_TILES = 1.5;

/** Décalage horizontal maximal entre les deux robots au moment du départ. */
export const SOLVER_REPEL_MAX_OFFSET_TILES = 1.5;

/** Nombre maximal de signaux (boutons, leviers) modélisés par le solveur. */
export const SOLVER_MAX_GATES = 8;

/**
 * Coût d'une bascule de levier dans la recherche de plan.
 *
 * Plus cher qu'un pas de marche (on ne bascule pas un levier « en passant »),
 * beaucoup moins cher qu'un passage coopératif : un aller-retour au levier
 * reste préférable à un hissage inutile.
 */
export const SOLVER_LATCH_EDGE_COST = 4;

/**
 * Clé de session où le client range le jeton de lecture des brouillons.
 *
 * Le mode « drafts » s'active en ouvrant le jeu avec `?drafts=<token>` : le
 * jeton reste dans l'onglet (sessionStorage), n'est jamais écrit dans l'URL
 * partagée et sert d'en-tête `Authorization` sur l'API de contenu.
 */
export const DRAFTS_TOKEN_STORAGE_KEY = 'md:drafts-token';

// ---------------------------------------------------------------------------
// Progression du joueur (concept §4.3).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Comptes joueurs (OBLIGATOIRES depuis la v1.3.0) : pseudonyme + mot de passe.
// La progression vit en base côté serveur — plus aucun stockage local.
// ---------------------------------------------------------------------------

/** Préfixe des routes de compte (`/register`, `/login`, `/progress`…). */
export const ACCOUNT_API_PREFIX = '/api/account';

/** Clé de stockage local de la session de compte (`{ token, pseudo }`). */
export const ACCOUNT_STORAGE_KEY = 'md:account';

/** Nombre maximal de niveaux acceptés dans une progression poussée au serveur. */
export const ACCOUNT_PROGRESS_MAX_LEVELS = 1000;

// ---------------------------------------------------------------------------
// Feedback visuel et sonore (concept §9).
//
// Ces réglages ne touchent JAMAIS la simulation : ils décrivent seulement ce
// que le client affiche ou joue en observant l'état. La simulation reste pure
// et déterministe (spec §3).
// ---------------------------------------------------------------------------

/** Nombre maximal de particules de limaille affichées simultanément. */
export const PARTICLE_MAX = 220;

/** Particules émises par seconde et par champ magnétique actif. */
export const PARTICLE_EMIT_PER_SECOND = 45;

/** Durée de vie moyenne d'une particule, en secondes. */
export const PARTICLE_LIFE_SECONDS = 1.1;

/** Attraction visuelle d'une particule vers le robot, en tuiles/s². */
export const PARTICLE_PULL_TILES_PER_S2 = 26;

/** Fraction de vitesse conservée par une particule après une seconde. */
export const PARTICLE_DRAG_PER_SECOND = 0.08;

/** Amplitude de la secousse d'écran déclenchée par une répulsion, en tuiles. */
export const SHAKE_REPEL_TILES = 0.35;

/** Amplitude de la secousse d'écran à la destruction d'un robot, en tuiles. */
export const SHAKE_DEATH_TILES = 0.5;

/** Fraction de la secousse conservée après une seconde. */
export const SHAKE_DAMPING_PER_SECOND = 0.0005;

/** Clé de stockage local des préférences (son, vibration). */
export const SETTINGS_STORAGE_KEY = 'md:settings';

// ---------------------------------------------------------------------------
// PWA (concept §5.2 : jouable sur deux smartphones).
// ---------------------------------------------------------------------------

/** Chemin du manifeste d'application web. */
export const MANIFEST_PATH = '/manifest.webmanifest';

/** Chemin du service worker (portée racine). */
export const SERVICE_WORKER_PATH = '/sw.js';

/** Nom du cache du service worker ; le suffixe change à chaque déploiement. */
export const SERVICE_WORKER_CACHE = 'magnetic-duo-shell';
